/**
 * ADR-0207 — the workspace's own AI provider: shared console helpers for the
 * integration form, its detail page, the Settings AI card and the AI panels.
 * The hub validates everything again; these checks only give the admin an
 * answer on blur instead of on save.
 */

export type AiProviderKind = "anthropic" | "openai" | "azure_foundry";
export type AiApiFamily = "anthropic_messages" | "openai_chat" | "openai_responses";

export const AI_PROVIDER_OPTIONS: ReadonlyArray<{ value: AiProviderKind; label: string }> = [
  { value: "anthropic", label: "Anthropic" },
  { value: "openai", label: "OpenAI" },
  { value: "azure_foundry", label: "Microsoft Azure AI Foundry" }
];

export function aiProviderLabel(kind: string | null | undefined): string {
  return AI_PROVIDER_OPTIONS.find((o) => o.value === kind)?.label ?? "AI provider";
}

export interface AiProviderConfigShape {
  provider_kind: AiProviderKind;
  endpoint?: string;
  deployments?: Record<string, string>;
  web_search_enabled: boolean;
}

/** Same pattern the hub enforces: scheme and host only, after one trailing "/". */
const FOUNDRY_ENDPOINT_RE = /^https:\/\/[a-z0-9][a-z0-9-]{1,62}\.(services\.ai\.azure\.com|openai\.azure\.com)$/i;

export const FOUNDRY_ENDPOINT_PLACEHOLDER = "https://your-resource.services.ai.azure.com";
export const FOUNDRY_ENDPOINT_ERROR =
  "Use your Foundry resource's endpoint, like https://your-resource.services.ai.azure.com";

export function isValidFoundryEndpoint(raw: string): boolean {
  const trimmed = raw.endsWith("/") ? raw.slice(0, -1) : raw;
  return FOUNDRY_ENDPOINT_RE.test(trimmed);
}

/** Which API families a provider can serve at all (Foundry serves all three, deployment by deployment). */
export function providerServesFamily(kind: AiProviderKind, family: string | null | undefined): boolean {
  if (!family) return false;
  if (kind === "azure_foundry") return family === "anthropic_messages" || family === "openai_chat" || family === "openai_responses";
  if (kind === "anthropic") return family === "anthropic_messages";
  return family === "openai_chat" || family === "openai_responses";
}

/** Help under "Allow web search", per provider (spec §3.1). */
export function webSearchHelp(kind: AiProviderKind): string | null {
  if (kind === "azure_foundry") {
    return "Web searches go through Microsoft's Bing service, which is outside your Azure data boundary and not covered by Microsoft's data protection terms.";
  }
  if (kind === "openai") return "Web searches run on OpenAI's search tool.";
  return null;
}

export interface ModelCandidates {
  candidates: Array<{ model_id: string; family: AiApiFamily }>;
  web_search_model_family: AiApiFamily | null;
}

export interface AiProviderStatus {
  state: "connected" | "shared" | "off";
  can_manage: boolean;
  provider_kind?: AiProviderKind;
  last_test_status?: "success" | "failed" | null;
  last_test_at?: string | null;
  integration_id?: string;
}

export const AI_PROVIDER_NEW_PATH = "/app/admin/integrations/new?kind=ai_provider";

export function aiProviderIntegrationPath(integrationId: string): string {
  return `/app/admin/integrations/${integrationId}`;
}

export interface AiProviderTestResponse {
  ok: boolean;
  models: Array<{ model_id: string; ok: boolean; detail: string }>;
  message?: string;
}

/** The test modal's headline (spec §3.2). */
export function testHeadline(result: AiProviderTestResponse): string {
  const answered = result.models.filter((m) => m.ok).length;
  if (result.models.length === 0) return result.message ?? "No models to test.";
  if (result.ok) return `Connected. ${answered} of ${result.models.length} models answered.`;
  return result.message ?? "No model answered.";
}
